/**
 * Origin Protocol — a focused, editable view of a domain's edge→origin
 * connection settings, for the Multi-CDN tab. Pick a domain, then change the
 * origin protocol (Match Viewer / HTTP / HTTPS), the HTTP/HTTPS ports, and SNI.
 *
 * Reads the full domain detail (`GET /waf/domains/detail`) but only surfaces the
 * origin protocol/ports/SNI fields; every change fires a partial PATCH
 * (`PATCH /waf/domains/settings` with `{ origin: { … } }`), and the detail is
 * refetched so each control reflects the true persisted state.
 */
import { useEffect, useState } from 'react';
import { Server } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import {
  Skeleton,
  Input,
  Button,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafDomainDetail,
  useUpdateWafDomainSettings,
  wafExtractError,
  type DomainOrigin,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import styles from './WafOriginProtocol.module.css';

/** Edge→origin protocol policy (vendor `ssl_policies` ∈ match_view | http | https). */
const PROTOCOL_OPTIONS = [
  { value: 'match_view', label: 'Match Viewer' },
  { value: 'http', label: 'HTTP' },
  { value: 'https', label: 'HTTPS' },
];

/** Normalize whatever the detail returns to one of the three canonical tokens. */
function normalizeProtocol(v: string | undefined): string {
  const t = (v ?? '').toLowerCase().replace(/\s+/g, '_');
  if (t === 'http') return 'http';
  if (t === 'https') return 'https';
  return 'match_view'; // match_view / match_viewer / empty / unknown
}

const MIN_PORT = 1;
const MAX_PORT = 65535;

/** A label / control row inside the settings grid. */
function Row({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <>
      <div className={styles.label}>{label}</div>
      <div>
        {children}
        {hint ? (
          <div style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)', marginTop: 4 }}>
            {hint}
          </div>
        ) : null}
      </div>
    </>
  );
}

/**
 * A port number input that holds its own draft. A Save / Cancel pair appears
 * once the draft differs from the persisted value; Save is disabled while the
 * draft is empty / non-integer / out of `[1, 65535]`. Enter also saves.
 */
function PortField({
  label,
  value,
  disabled,
  onCommit,
}: {
  label: string;
  value: number | undefined;
  disabled: boolean;
  onCommit: (port: number) => void;
}) {
  const base = value != null ? String(value) : '';
  const [draft, setDraft] = useState(base);
  // Re-sync the draft whenever the persisted value changes (refetch / domain switch).
  useEffect(() => {
    setDraft(value != null ? String(value) : '');
  }, [value]);

  const n = Number(draft);
  const validDraft = !!draft.trim() && Number.isInteger(n) && n >= MIN_PORT && n <= MAX_PORT;
  const dirty = draft !== base;

  const commit = () => {
    if (!validDraft || n === value) return;
    onCommit(n);
  };

  return (
    <Row label={label}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <Input
          type="number"
          min={MIN_PORT}
          max={MAX_PORT}
          className={`${styles.control} ${styles.noSpin}`}
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') setDraft(base);
          }}
        />
        {dirty && (
          <>
            <Button size="sm" disabled={disabled || !validDraft} onClick={commit}>
              Save
            </Button>
            <Button size="sm" variant="ghost" disabled={disabled} onClick={() => setDraft(base)}>
              Cancel
            </Button>
          </>
        )}
      </div>
    </Row>
  );
}

/** The editable origin-protocol card for one resolved domain. */
function OriginCard({
  serviceId,
  domain,
  origin,
}: {
  serviceId: string;
  domain: string;
  origin: DomainOrigin;
}) {
  const { show } = useToast();
  const mut = useUpdateWafDomainSettings(serviceId, domain, {
    onSuccess: () => { show({ tone: 'success', title: 'Origin updated' }); trackFeatureAction('waf', 'origin', 'edit'); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }),
  });

  const protocol = normalizeProtocol(origin.ssl_policies);
  // SNI only applies to TLS connections to the origin (HTTPS / Match Viewer).
  const sniDisabled = mut.isPending || protocol === 'http';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Origin protocol</CardTitle>
      </CardHeader>
      <CardBody>
        <div className={styles.grid}>
          <Row label="Protocol" hint="How the edge connects to your origin.">
            <Select
              value={protocol}
              disabled={mut.isPending}
              onValueChange={(v) => mut.mutate({ origin: { ssl_policies: v } })}
            >
              <SelectTrigger aria-label="Origin protocol" className={styles.control}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROTOCOL_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Row>

          <PortField
            label="HTTP port"
            value={origin.port_http}
            disabled={mut.isPending}
            onCommit={(port_http) => mut.mutate({ origin: { port_http } })}
          />

          <PortField
            label="HTTPS port"
            value={origin.port_https}
            disabled={mut.isPending}
            onCommit={(port_https) => mut.mutate({ origin: { port_https } })}
          />

          <Row label="SNI" hint="Send SNI on HTTPS connections to the origin.">
            <Switch
              checked={origin.sni}
              disabled={sniDisabled}
              onCheckedChange={(sni) => mut.mutate({ origin: { sni } })}
            />
          </Row>
        </div>
      </CardBody>
    </Card>
  );
}

/** Loads the detail for a resolved subdomain and renders the origin card. */
function OriginSection({ serviceId, domain }: { serviceId: string; domain: string }) {
  const detailQ = useWafDomainDetail(serviceId, domain);

  if (detailQ.isLoading) return <Skeleton style={{ height: 220 }} />;
  if (detailQ.isError) {
    return (
      <Alert
        tone="danger"
        title="Couldn't load origin settings"
        actions={<button onClick={() => detailQ.refetch()}>Retry</button>}
      >
        {wafExtractError(detailQ.error, 'The origin settings for this domain failed to load.')}
      </Alert>
    );
  }
  if (!detailQ.data) return null;
  return <OriginCard serviceId={serviceId} domain={domain} origin={detailQ.data.origin} />;
}

export function WafOriginProtocol({ serviceId }: { serviceId: string }) {
  const [scope, setScope] = useState<WafScope>({});
  const domain = scope.subdomain;
  // A zone is selected but has no proxied subdomain → nothing to manage.
  const noSubdomain = !!scope.zone && !domain;

  return (
    <Container size="xl">
      <PageHeader
        icon={<Server size={22} />}
        title="Origin protocol"
        description="Configure how the edge connects to your origin for the selected subdomain."
      />

      <div className={styles.scopeBar}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<Server size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain to configure origin settings for. Proxy a record first."
        />
      ) : !domain ? (
        <EmptyState
          icon={<Server size={20} />}
          title="Select a subdomain"
          description="Pick a domain and subdomain above to configure its origin protocol."
        />
      ) : (
        <OriginSection serviceId={serviceId} domain={domain} />
      )}
    </Container>
  );
}

export default WafOriginProtocol;
